import * as THREE from 'three';
import { configureStableTextureSampling } from './texture-sampling';

// ---------------------------------------------------------------------------
// Player-only texture resolution limits (URL textures + embedded GLB maps)
// ---------------------------------------------------------------------------

export type PlayerTextureMaxEdge = 'off' | '4096' | '2048' | '1024';

export type PlayerTextureResolutionScale = 1 | 0.5 | 0.25;

export interface PlayerTextureQualityOptions {
  /** Cap longest edge (after scale). `off` = no cap. */
  maxEdge: PlayerTextureMaxEdge;
  /** Multiply width/height before cap (1 = 100%, 0.5 = 50%, 0.25 = 25%). */
  resolutionScale: PlayerTextureResolutionScale;
  /** When false, `?nowebp=1` is appended so the API serves the original format. */
  webp: boolean;
  /**
   * Optional hard cap for CPU graph bakes (blend / exposure / invert / UV transform).
   * Independent of download `maxEdge` — wood on iPad OOMs when baking at 2k.
   */
  cpuBakeMaxEdge?: number;
}

const defaultOptions: PlayerTextureQualityOptions = {
  maxEdge: 'off',
  resolutionScale: 1,
  webp: true,
  cpuBakeMaxEdge: undefined,
};

let options: PlayerTextureQualityOptions = { ...defaultOptions };

const cacheClearHooks: Array<() => void> = [];
const qualitySubscribers = new Set<() => void>();
let qualityEpoch = 0;

export function registerPlayerTextureCacheClearHook(fn: () => void): void {
  cacheClearHooks.push(fn);
}

function runCacheClears(): void {
  for (const fn of cacheClearHooks) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function bumpEpoch(): void {
  qualityEpoch++;
  // ConfiguratorPlayer applies texture quality during render (so the first GLB clone
  // sees the right epoch). Notifying useSyncExternalStore subscribers synchronously
  // would update ViewerCanvas/GlbModel mid-render → React "setState while rendering".
  queueMicrotask(() => {
    for (const cb of qualitySubscribers) {
      try {
        cb();
      } catch {
        /* ignore */
      }
    }
  });
}

/** Subscribe for React `useSyncExternalStore` when GLB clones must rebuild. */
export function subscribePlayerTextureQuality(onChange: () => void): () => void {
  qualitySubscribers.add(onChange);
  return () => qualitySubscribers.delete(onChange);
}

export function getPlayerTextureQualityEpoch(): number {
  return qualityEpoch;
}

export function getPlayerTextureQualityOptions(): Readonly<PlayerTextureQualityOptions> {
  return options;
}

/** Stable string for texture loader cache keys (URL + linear + quality). */
export function getTextureQualityCacheSuffix(): string {
  const bake = options.cpuBakeMaxEdge ?? 0;
  return `q_${options.maxEdge}_s${options.resolutionScale}_w${options.webp ? 1 : 0}_b${bake}`;
}

export function isPlayerTextureQualityPassive(): boolean {
  return (
    options.maxEdge === 'off' &&
    options.resolutionScale === 1 &&
    options.webp &&
    options.cpuBakeMaxEdge == null
  );
}

/**
 * Percent-encode each path segment so spaces and non-ASCII filenames load reliably
 * (`TextureLoader` / `fetch` expect a valid URL). Idempotent when segments are already encoded.
 */
export function encodeTextureUrlForFetch(url: string): string {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return url;

  const qIdx = url.indexOf('?');
  const pathEnd = qIdx >= 0 ? qIdx : url.length;
  let pathPart = url.slice(0, pathEnd);
  const queryAndHash = url.slice(pathEnd);

  const encodeSegments = (path: string): string =>
    path
      .split('/')
      .map((seg) => {
        if (!seg) return '';
        try {
          return encodeURIComponent(decodeURIComponent(seg.replace(/\+/g, '%20')));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join('/');

  if (pathPart.startsWith('/')) {
    return encodeSegments(pathPart) + queryAndHash;
  }

  const m = pathPart.match(/^(https?:\/\/[^/]+)(\/.*)?$/i);
  if (m) {
    const origin = m[1];
    const p = m[2] || '/';
    return origin + encodeSegments(p) + queryAndHash;
  }

  return url;
}

/**
 * Append `?q=1k` or `?q=2k` to `/uploads/` image URLs when a texture quality
 * cap is active. The API serves pre-generated WebP variants at that resolution.
 * WebP content-negotiation (via Accept header) happens automatically in the browser.
 */
export function optimizeTextureUrl(url: string): string {
  const enc = encodeTextureUrlForFetch(url);
  const pathOnly = enc.split(/[?#]/, 1)[0] ?? enc;
  // Match a `/uploads/<file>` segment ANYWHERE in the path. The player is usually
  // mounted under a base path (e.g. `/projects/mh-ai/c3d/uploads/...`), so the old
  // root-only check (`/uploads/` at index 0, or `https://host/uploads/...`) matched
  // nothing in those deployments and silently shipped full-res maps. The API/proxy
  // serves the pre-generated `?q=1k` WebP variant at whatever prefix the request uses.
  const uploadsIdx = pathOnly.indexOf('/uploads/');
  const uploadsRel = uploadsIdx >= 0 ? pathOnly.slice(uploadsIdx) : null;
  if (!uploadsRel) return enc;

  const ext = uploadsRel.split('.').pop()?.toLowerCase();
  if (ext !== 'png' && ext !== 'jpg' && ext !== 'jpeg') return enc;

  const parts: string[] = [];
  const edge = options.maxEdge;
  // `4k` / `4096` still need a server-side cap — there is no `.4k.webp`, and without `q=`
  // the API serves the full master webp (often 4096×2048 oak maps). Prefer 2k variants.
  if (edge !== 'off') {
    const q = edge === '1024' ? '1k' : edge === '2048' || edge === '4096' ? '2k' : null;
    if (q) parts.push(`q=${q}`);
  }
  if (!options.webp) parts.push('nowebp=1');
  if (parts.length === 0) return enc;
  const sep = enc.includes('?') ? '&' : '?';
  return `${enc}${sep}${parts.join('&')}`;
}

export function ensurePlayerTextureQuality(patch: Partial<PlayerTextureQualityOptions>): boolean {
  const next: PlayerTextureQualityOptions = {
    maxEdge: patch.maxEdge ?? options.maxEdge,
    resolutionScale: (patch.resolutionScale ?? options.resolutionScale) as PlayerTextureResolutionScale,
    webp: patch.webp ?? options.webp,
    cpuBakeMaxEdge:
      patch.cpuBakeMaxEdge !== undefined ? patch.cpuBakeMaxEdge : options.cpuBakeMaxEdge,
  };
  if (next.resolutionScale !== 1 && next.resolutionScale !== 0.5 && next.resolutionScale !== 0.25) {
    next.resolutionScale = 1;
  }
  const unchanged =
    next.maxEdge === options.maxEdge &&
    next.resolutionScale === options.resolutionScale &&
    next.webp === options.webp &&
    next.cpuBakeMaxEdge === options.cpuBakeMaxEdge;
  if (unchanged) return false;
  options = next;
  runCacheClears();
  return true;
}

export function setPlayerTextureQuality(patch: Partial<PlayerTextureQualityOptions>): void {
  if (!ensurePlayerTextureQuality(patch)) return;
  bumpEpoch();
}

export function resetPlayerTextureQuality(): void {
  options = { ...defaultOptions };
  runCacheClears();
  bumpEpoch();
}

export type PlayerTextureQualityPreset = 'full' | '4k' | '2k' | '1k' | 'half' | 'quarter';

/** Apply a named preset (for URL `?tex=` or UI). */
export function applyPlayerTextureQualityPreset(
  preset: string | null | undefined,
  opts?: { /** Update options without notifying React (safe during render). */ silent?: boolean },
): void {
  const apply = opts?.silent ? ensurePlayerTextureQuality : setPlayerTextureQuality;
  const p = String(preset || 'full').toLowerCase().trim();
  switch (p) {
    case 'full':
    case 'off':
    case '100':
      apply({ maxEdge: 'off', resolutionScale: 1 });
      break;
    case '4k':
    case '4096':
      apply({ maxEdge: '4096', resolutionScale: 1 });
      break;
    case '2k':
    case '2048':
      apply({ maxEdge: '2048', resolutionScale: 1 });
      break;
    case '1k':
    case '1024':
      apply({ maxEdge: '1024', resolutionScale: 1 });
      break;
    case 'half':
    case '50':
    case '0.5':
      apply({ maxEdge: 'off', resolutionScale: 0.5 });
      break;
    case 'quarter':
    case '25':
    case '0.25':
      apply({ maxEdge: 'off', resolutionScale: 0.25 });
      break;
    default:
      apply({ maxEdge: 'off', resolutionScale: 1 });
  }
}

function maxEdgeNumber(edge: PlayerTextureMaxEdge): number {
  switch (edge) {
    case '4096':
      return 4096;
    case '2048':
      return 2048;
    case '1024':
      return 1024;
    default:
      return Infinity;
  }
}

export function computePlayerTextureDimensions(
  width: number,
  height: number,
): { w: number; h: number } | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) return null;
  const scale = options.resolutionScale;
  let w = Math.max(1, Math.floor(width * scale));
  let h = Math.max(1, Math.floor(height * scale));
  const cap = maxEdgeNumber(options.maxEdge);
  const long = Math.max(w, h);
  if (long > cap) {
    const f = cap / long;
    w = Math.max(1, Math.floor(w * f));
    h = Math.max(1, Math.floor(h * f));
  }
  if (w === width && h === height) return null;
  return { w, h };
}

function readImageSize(image: unknown): { w: number; h: number } | null {
  if (!image) return null;
  if (image instanceof HTMLImageElement) {
    const w = image.naturalWidth || image.width;
    const h = image.naturalHeight || image.height;
    return w > 0 && h > 0 ? { w, h } : null;
  }
  if (
    image instanceof HTMLCanvasElement ||
    image instanceof ImageBitmap ||
    (typeof OffscreenCanvas !== 'undefined' && image instanceof OffscreenCanvas)
  ) {
    const el = image as { width: number; height: number };
    return el.width > 0 && el.height > 0 ? { w: el.width, h: el.height } : null;
  }
  return null;
}

/**
 * Replace `texture.image` with a downscaled canvas when quality options demand it.
 * Safe on clones; skips DataTexture / CompressedTexture / empty images.
 */
export function applyPlayerTextureDownscale(tex: THREE.Texture): void {
  if (isPlayerTextureQualityPassive()) return;
  if ((tex as THREE.DataTexture).isDataTexture) return;
  if ((tex as any).isCompressedTexture) return;
  const img = tex.image;
  const size = readImageSize(img);
  if (!size) return;
  const target = computePlayerTextureDimensions(size.w, size.h);
  if (!target) return;

  const canvas = document.createElement('canvas');
  canvas.width = target.w;
  canvas.height = target.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  try {
    ctx.drawImage(img as CanvasImageSource, 0, 0, target.w, target.h);
  } catch {
    return;
  }
  tex.image = canvas;
  tex.needsUpdate = true;
  configureStableTextureSampling(tex);
}

const MATERIAL_MAP_KEYS = [
  'map',
  'normalMap',
  'bumpMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'aoMap',
  'displacementMap',
  'alphaMap',
  'anisotropyMap',
  'clearcoatMap',
  'clearcoatRoughnessMap',
  'clearcoatNormalMap',
  'lightMap',
  'specularMap',
] as const;

/** Clone material and give each map texture a downscaled copy (does not mutate GLTF cache). */
export function cloneMaterialMapsForPlayer(mat: THREE.Material): THREE.Material {
  const m = mat.clone();
  for (const key of MATERIAL_MAP_KEYS) {
    const t = (m as any)[key] as THREE.Texture | undefined;
    if (!t?.isTexture) continue;
    if ((t as THREE.DataTexture).isDataTexture || (t as any).isCompressedTexture) continue;
    const nt = t.clone();
    nt.userData = { ...t.userData };
    applyPlayerTextureDownscale(nt);
    (m as any)[key] = nt;
  }
  return m;
}

/** After `scene.clone(true)`, replace shared GLTF textures with downscaled clones on this root only. */
export function detachEmbeddedTexturesForPlayer(root: THREE.Object3D): void {
  if (isPlayerTextureQualityPassive()) return;
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (let i = 0; i < mats.length; i++) {
      const mat = mats[i];
      if (!mat) continue;
      const cloned = cloneMaterialMapsForPlayer(mat);
      if (Array.isArray(obj.material)) {
        (obj.material as THREE.Material[])[i] = cloned;
      } else {
        obj.material = cloned;
      }
    }
  });
}
